/**
 * Display labels for filter keys shown in "N/A: Filter1, Filter2" under tile headings.
 * Used when a report block does not support an applied filter.
 */
export const FILTER_KEY_TO_DISPLAY_LABEL: Record<string, string> = {
  date: 'Time',
  survey_name: 'Survey name',
  job_name: 'Job name',
  job_type: 'Job type',
  job_status: 'Job status',
  primary_recruiter: 'Primary recruiter',
  primary_hm: 'Primary hiring manager',
  department: 'Department',
  appl_location: 'Office',
  users: 'User',
  appl_contact_source: 'Source (on application)',
  root_department: 'Root department',
  appl_credited_to: 'Application credited to',
  user_role: 'Job role',
  appl_stage: 'Application stage',
  job_location: 'Location (on job)',
  office: 'Office',
  flow_id: 'Flow',
  recruiter_id: 'Recruiter',
  ats_department_id: 'Department',
  ats_job_id: 'Job',
  application_creator: 'Application creator',
  hired_by: 'Hired by',
  scheduled_by: 'Scheduled by',
  rejected_by: 'Rejected by',
  offer_by: 'Offer created by',
  contact_created_by: 'Contact created by',
  created_by: 'Job created by',
  requester: 'Requisition created by',
  interview_location: 'Interview location',
  interview_type: 'Interview type',
  duration_bucket: 'Interview duration',
  rejection_reason: 'Rejection reason',
  rejection_type: 'Rejection type',
  rejected_stage: 'Rejected stage',
  rejected_by_type: 'Rejected by',
  close_reason: 'Job close reason',
  contact_source: 'Source (on contact)',
  contact_credited_to: 'Contact credited to',
  gender: 'Gender',
  contact_location: 'Contact location',
  experience_bucket: 'Experience buckets',
  eeoc_gender: 'Gender',
  eeoc_race: 'Race',
  eeoc_veteran_status: 'Veteran status',
  eeoc_disability_status: 'Disability status',
  time_in_stage: 'Time in stage',
  is_overdue: 'Overdue candidates',
  requisition_type: 'Requisition type',
  requisition_status: 'Requisition status',
  recruiter: 'Recruiter',
  hiring_manager: 'Hiring manager',
  work_type: 'Work type',
  associated_job_name: 'Associated job name',
  associated_job_status: 'Associated job status',
};

function humanizeFilterKey(key: string): string {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/** Subject type for live (current snapshot) reports that do not use timeframe. */
export const LIVE_REPORT_SUBJECT_TYPE = 'live_job_pipeline';

/** Time-related keys to exclude from payload and filters for live reports. */
export const TIME_RELATED_KEYS_LIVE_REPORTS = [
  'date',
  'date_group',
  'compare',
] as const;

/**
 * Returns display labels for filters that are applied but not supported by the given report type.
 * Date (Time) is supported for all report types except live_job_pipeline.
 */
export function getUnsupportedFilterLabels(
  subjectType: string,
  filterByKeys: string[]
): string[] {
  const allowedKeys =
    CUSTOM_REPORT_FILTER_KEYS[
      subjectType as keyof typeof CUSTOM_REPORT_FILTER_KEYS
    ] ?? [];
  const isDateSupported = subjectType !== LIVE_REPORT_SUBJECT_TYPE;
  const unsupportedKeys = filterByKeys.filter((key) => {
    if (key === 'date') return !isDateSupported;
    return !allowedKeys.includes(key);
  });
  return unsupportedKeys.map(
    (key) => FILTER_KEY_TO_DISPLAY_LABEL[key] ?? humanizeFilterKey(key)
  );
}

/**
 * For non-static reports: when filter key is users,
 * replace with this key for the given report type before sending to API.
 */
export const USERS_KEY_BY_REPORT_TYPE: Record<string, string> = {
  pipeline: 'application_creator',
  [LIVE_REPORT_SUBJECT_TYPE]: 'application_creator',
  hires: 'hired_by',
  interviews: 'scheduled_by',
  rejections: 'rejected_by',
  offers: 'offer_by',
  jobs: 'created_by',
  contacts: 'contact_created_by',
  requisitions: 'requester',
  recruiter_performance: 'users',
};

export const CUSTOM_REPORT_FILTER_KEYS = {
  hires: [
    'job_name',
    'job_type',
    'job_status',
    'primary_recruiter',
    'primary_hm',
    'appl_contact_source',
    'root_department',
    'department',
    'appl_location',
    'appl_credited_to',
    'users',
    'user_role',
    'hired_by',
    'users',
  ],
  interviews: [
    'job_name',
    'job_type',
    'job_status',
    'primary_recruiter',
    'primary_hm',
    'appl_contact_source',
    'root_department',
    'department',
    'appl_location',
    'interview_location',
    'interview_type',
    'duration_bucket',
    'users',
    'user_role',
    'scheduled_by',
    'users',
  ],
  rejections: [
    'job_name',
    'job_type',
    'job_status',
    'primary_recruiter',
    'primary_hm',
    'appl_contact_source',
    'root_department',
    'department',
    'appl_location',
    'appl_credited_to',
    'rejection_reason',
    'rejection_type',
    'rejected_stage',
    'rejected_by_type',
    'users',
    'user_role',
    'rejected_by',
  ],
  pipeline: [
    'job_name',
    'job_type',
    'job_status',
    'primary_recruiter',
    'primary_hm',
    'appl_contact_source',
    'root_department',
    'department',
    'appl_location',
    'appl_stage',
    'rejected_by_type',
    'users',
    'application_creator',
    'bgv_risk_level',
  ],
  live_job_pipeline: [
    'job_name',
    'job_type',
    'time_in_stage',
    'is_overdue',
    'primary_recruiter',
    'primary_hm',
    'appl_contact_source',
    'appl_credited_to',
    'department',
    'office',
    'users',
    'application_creator',
    'bgv_risk_level',
  ],
  offers: [
    'job_name',
    'job_type',
    'job_status',
    'primary_recruiter',
    'primary_hm',
    'appl_contact_source',
    'root_department',
    'department',
    'appl_location',
    'rejection_reason',
    'users',
    'user_role',
    'offer_by',
    'users',
  ],
  jobs: [
    'job_name',
    'job_type',
    'job_status',
    'primary_recruiter',
    'primary_hm',
    'root_department',
    'department',
    'job_location',
    'close_reason',
    'users',
    'user_role',
    'created_by',
    'users',
  ],
  contacts: [
    'job_name',
    'contact_source',
    'contact_credited_to',
    'gender',
    'contact_location',
    'experience_bucket',
    'users',
    'user_role',
    'contact_created_by',
    'users',
    'bgv_risk_level',
  ],
  eeoc: [
    'job_name',
    'job_type',
    'job_status',
    'primary_recruiter',
    'primary_hm',
    'department',
    'appl_contact_source',
    'appl_location',
    'gender',
    'race',
    'veteran_status',
    'disability_status',
  ],
  // add back job_name
  flow_health: [
    'job_name',
    'flow_id',
    'recruiter_id',
    'ats_department_id',
    'ats_job_id',
  ],
  // add back job_name
  flow_user_activity: [
    'job_name',
    'flow_id',
    'recruiter_id',
    'ats_department_id',
    'ats_job_id',
  ],
  recruiter_performance: [
    'job_name',
    'job_type',
    'job_status',
    'primary_recruiter',
    'primary_hm',
    'appl_contact_source',
    'root_department',
    'department',
    'appl_location',
    'appl_credited_to',
    'users',
    'user_role',
    'users',
  ],
  requisitions: [
    'requisition_type',
    'requisition_status',
    'recruiter',
    'hiring_manager',
    'department',
    'office',
    'work_type',
    'associated_job_name',
    'associated_job_status',
    'requester',
    'users',
  ],
  ats_survey: [
    'job_name',
    'job_type',
    'job_status',
    'primary_recruiter',
    'primary_hm',
    'appl_contact_source',
    'root_department',
    'department',
    'appl_location',
    'survey_name',
  ],
};
